import { Box, Button, Checkbox, Divider, FormControlLabel, Modal, Stack, TextField, Typography } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { useEffect, useRef, useState } from 'react';
import { HttpMainApi } from '../../interface/main-api';
import { userState } from '../../interface/MainInterface';
import dayjs, { Dayjs } from 'dayjs';
import Toast from '../../utils/Toast';
import LoadingCircle from '../../utils/LoadingCircle';
import { ClassNames } from '@emotion/react';
import "./Calendar.css";
import koLocale from '@fullcalendar/core/locales/ko';

interface propsType {
  userState: userState;
  drawerWidth: number;
}

const mainApi = new HttpMainApi();

const Calendar = (props: propsType) => {
  const toastRef: any = useRef();
  const calendarRef = useRef<FullCalendar | null>(null);

  const [events, setEvents] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [newEventTitle, setNewEventTitle] = useState('');

  const [fromDate, setFromDate] = useState(dayjs().subtract(2, 'month').startOf('month').format('YYYYMMDD'));
  const [toDate, setToDate] = useState(dayjs().add(2, 'month').endOf('month').format('YYYYMMDD'));

  const [selectedStatsOption, setSelectedStatsOption] = useState("all");
  const [allIngList, setAllIngList] = useState([]);
  
  const statusOptions = ['COMPLETE', 'CREATE', 'RETURNED', 'REJECTED', 'HONGBO_ING', 'HONGBO_OK'];
  const [statusFilter, setStatusFilter] = useState<string[]>(statusOptions);

  const statusColorMap: Record<string, string> = {
    COMPLETE: '#5d88f1',
    CREATE: '#32bf95',
    RETURNED: '#515151',
    REJECTED: '#ef6961',
    HONGBO_ING: '#999999',
    HONGBO_OK: '#ffac0b'
  }

  useEffect(() => {
    getPressList();
    return () => {
      setIsLoading(false);
      setAllIngList([]);
      setFromDate(dayjs().subtract(2, 'month').startOf('month').format('YYYYMMDD'));
      setToDate(dayjs().add(2, 'month').endOf('month').format('YYYYMMDD'));
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.updateSize();
      }
    }, 300); // transition 후 업데이트할 수 있도록 약간의 delay

    return () => clearTimeout(timeout);
  }, [props.drawerWidth]);

  useEffect(() => {
    if (allIngList.length > 0) {
      const formattedList = allIngList
        .filter((item: any) => item.form_data['배포 희망일'])
        .map((item: any) => ({
          id: item.sk,
          title: item.title,
          start: extractDateFromText(item.form_data['배포 희망일']),
          allDay: true,
          classNames: [item.press_status],
      }));

      setEvents(formattedList);
    }
  }, [allIngList]);

  useEffect(() => {
    getPressList();
  }, [fromDate, toDate]);

  useEffect(() => {
    // console.log(statusFilter);
    // console.log("allIngList: ", allIngList);
    const filteredList = allIngList
      .filter((item: any) => item.form_data['배포 희망일'])
      .filter((item: any) => statusFilter.includes(item.press_status))
      .map((item: any) => ({
        id: item.sk,
        title: item.title,
        start: extractDateFromText(item.form_data['배포 희망일']),
        allDay: true,
        classNames: [item.press_status],
      }));

    // console.log("filteredList: ", filteredList);
    setEvents(filteredList);
  }, [statusFilter]);

  const extractDateFromText = (text: string) => {
    // 1. 한글 날짜 표현 정규화
    const normalized = text
      .replace(/년|\.|\//g, '-')
      .replace(/월/g, '-')
      .replace(/일/g, '')
      .replace(/\s+/g, '');

    // 2. 날짜 포맷 인식: YYYY-MM-DD
    const datePattern = /(\d{4})-(\d{1,2})-(\d{1,2})/;
    const match = normalized.match(datePattern);

    if (!match) return null;

    const [, year, month, day] = match

    // 3. YYYYMMDD 형식으로 반환
    const paddedMonth = month.padStart(2, '0');
    const paddedDay = day.padStart(2, '0');

    return `${year}${paddedMonth}${paddedDay}`;
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      COMPLETE: '완료',
      CREATE: '작성중',
      RETURNED: '반려',
      REJECTED: '보완 요청',
      HONGBO_ING: '홍보실 접수 대기',
      HONGBO_OK: '홍보실 접수'
    }

    return map[status] || '알 수 없음';
  }

  const formatDateToYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  const getPressList = async () => {
    setIsLoading(true);

    const param: any = {
      command: "get_calendar_press_list",
      user_id: props.userState.id,
      from_date: fromDate,
      to_date: toDate,
      sortForwrad: false,
      selectedStatsOption: selectedStatsOption
    };

    const res = await mainApi.post(param);

    if (res.code === "200") {
      setIsLoading(false);
      setAllIngList(res.response.work_list);
    } else {
      setIsLoading(false);
      console.error(res.response.error_msg);
      toastRef.current?.toast("" + res.response.error_msg, "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    }
  };

  const handleDateClick = (arg: DateClickArg) => {
    setSelectedDate(arg.dateStr);
    setOpen(true);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter((prev) => 
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleAddEvent = () => {
    if (newEventTitle.trim() === '') return;
    setEvents([
      ...events,
      {
        title: newEventTitle,
        start: selectedDate,
        allDay: true
      }
    ]);
    setOpen(false);
    setNewEventTitle('');
  };

  return (
    <>
      <Box display='flex' height='100%'>
        {/* 사이드 체크박스 리스트 */}
        <Box 
          minWidth={200}
          maxWidth={240}
          p={'30px 20px'} 
          borderRight="1px solid #ccc"
          bgcolor='#f9f9f9'
          display='flex'
          flexDirection='column'
        >
          <Typography fontWeight="600" fontSize="1rem" sx={{ mb: 1 }}>상태 필터</Typography>
            {statusOptions.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={statusFilter.includes(status)}
                    onChange={() => handleStatusChange(status)}
                    sx={{
                      color: statusColorMap[status],
                      '&.Mui-checked': {
                        color: statusColorMap[status],
                      },
                      padding: '7px 7px 7px 0px',
                    }}
                  />
                }
                label={getStatusLabel(status)}
                sx={{ margin: 0 }}
              />
            ))}

            {/* 배포 희망일 없는 데이터 표시 영역 */}
            <Divider sx={{ my: '20px' }} />
            <Typography fontWeight="600" fontSize="1rem" sx={{ mb: 1 }}>배포 희망일 없음</Typography>
            <Stack spacing={1} maxHeight={'200px'} overflow={'auto'}>
              {allIngList.filter((item: any) => !item.form_data['배포 희망일']).map((item: any) => (
                <Box 
                  key={item.sk} 
                  p={'7px'} 
                  bgcolor="#efefef" 
                  borderRadius="4px"
                  onClick={() => {
                    window.location.href = `/makePress/${item.sk}`;
                  }}
                >
                  <Typography variant="body2">{item.title || '제목 없음'}</Typography>
                </Box>
              ))}
            </Stack>
        </Box>

        <Box flex={1} p={2}>
          <FullCalendar
            ref={calendarRef}
            locale="ko" // 실제로 사용할 locale 설정 
            locales={[koLocale]}  // 지원하는 locale 목록에 한국어 포함
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView='dayGridMonth'
            headerToolbar={{
              left: 'prev, next',
              center: 'title',
              // right: 'dayGridMonth, timeGridWeek, timeGridDay'
              right: 'today, dayGridMonth'
            }}
            datesSet={(arg) => {
              // arg.start: 현재 뷰의 시작 날짜 (Date 객체)
              // arg.end: 현재 뷰의 끝 날짜 (Date 객체)

              // sk(만들어진 날짜)는 202506인데, 배포 희망일이 202507일 경우 (월이 다를 경우를 위해서)
              const twoMonthsBefore = dayjs(arg.start).subtract(2, 'month').startOf('month').format('YYYYMMDD');
              setFromDate(twoMonthsBefore);

              const endOfCurrentMonth = dayjs(arg.end).add(2, 'month').endOf('month').format('YYYYMMDD');
              setToDate(endOfCurrentMonth);
            }}
            events={events}
            eventClick={(info) => {
              const eventId = info.event.id;
              window.location.href = `/makePress/${eventId}`;
            }}
            dayMaxEvents={3} // 하루에 최대 3개까지만 보여주고 나머지는 more
            dayMaxEventRows={3}        // 최대 3줄까지 이벤트 표시
            dayCellContent={(arg) => {
              return arg.date.getDate().toString(); // 숫자만 표시
            }}
            moreLinkContent={(arg) => (
              <div style={{ margin: '4px 3px'}}>{`${arg.num}개 더보기`}</div>
            )}
            dateClick={handleDateClick}
            height='auto'
            contentHeight="auto"         // 내용 높이 자동으로 맞춤
            expandRows={false}           // 주마다 높이 자동 확장 막기
          />
        </Box>
        
        {/* <Modal open={open} onClose={() => setOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              minWidth: 300,
            }}
          >
            <h3>{selectedDate}에 일정 추가</h3>
            <TextField
              fullWidth
              placeholder="일정 제목"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
              sx={{ mt: 2, mb: 2 }}
            />
            <Button variant="contained" onClick={handleAddEvent} fullWidth>
              추가하기
            </Button>
          </Box>
        </Modal> */}
      </Box>

      <Toast ref={toastRef} />
      <LoadingCircle loading={isLoading} />
    </>
  )

}

export default Calendar;